import { useDeferredValue, useEffect, useMemo, useState } from 'react';

import {
  isCampaignSupportedPlatform,
  toCampaignPlatform,
  type CampaignCreatorCountRequest,
} from '@/entities/campaign';
import type {
  CreatorFilterOption,
  CreatorLabelFilterOption,
  CreatorPlatformFilterOption,
  CreatorTabFilterRequest,
} from '@/features/campaign/ui/campaign-detail/hooks';

type Props = {
  filterRequest: CreatorTabFilterRequest | null;
};

export function useCampaignCreatorFilter({ filterRequest }: Props) {
  const [creatorFilter, setCreatorFilter] = useState<CreatorFilterOption>('all');
  const [creatorSearchQuery, setCreatorSearchQuery] = useState('');
  const [creatorPlatformFilter, setCreatorPlatformFilter] =
    useState<CreatorPlatformFilterOption>(null);
  const [creatorLabelFilter, setCreatorLabelFilter] = useState<CreatorLabelFilterOption>(null);
  const deferredCreatorSearchQuery = useDeferredValue(creatorSearchQuery.trim().toLowerCase());
  const normalizedCreatorSearchQuery = deferredCreatorSearchQuery || undefined;

  useEffect(() => {
    if (!filterRequest) return;

    setCreatorFilter(filterRequest.filter);
  }, [filterRequest]);

  const campaignCreatorRequestParams = useMemo<CampaignCreatorCountRequest>(
    () => ({
      platform:
        creatorPlatformFilter && isCampaignSupportedPlatform(creatorPlatformFilter)
          ? toCampaignPlatform(creatorPlatformFilter)
          : undefined,
      label: creatorLabelFilter === null ? undefined : creatorLabelFilter,
      name: normalizedCreatorSearchQuery,
    }),
    [creatorLabelFilter, creatorPlatformFilter, normalizedCreatorSearchQuery],
  );

  const paginationResetKey = useMemo(
    () => [creatorFilter, creatorLabelFilter, creatorPlatformFilter, normalizedCreatorSearchQuery],
    [creatorFilter, creatorLabelFilter, creatorPlatformFilter, normalizedCreatorSearchQuery],
  );

  return {
    campaignCreatorRequestParams,
    creatorFilter,
    creatorLabelFilter,
    creatorPlatformFilter,
    creatorSearchQuery,
    isPendingCreatorFilter: creatorFilter === 'pending',
    normalizedCreatorSearchQuery,
    paginationResetKey,
    setCreatorFilter,
    setCreatorLabelFilter,
    setCreatorPlatformFilter,
    setCreatorSearchQuery,
  };
}
